import {IGameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/IGameConfig";
import {BetWinMode} from "@nolimitcity/slot-game/bin/core/gametype/BetWinMode";
import {SpinMode} from "@nolimitcity/slot-game/bin/core/gametype/SpinMode";
import {IReelSets} from "@nolimitcity/slot-game/bin/core/reel/reelset/IReelSets";
import {IDeviceDependentConfig} from "@nolimitcity/slot-game/bin/core/useragent/UserAgent";
import {IWinRatios, WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {GGGameAssets} from "./GGGameAssets";
import {IGGLayerConfig} from "./stage/IGGLayerConfig";
import {LayerScaleOption} from "@nolimitcity/slot-game/bin/core/stage/LayerScaleOption";
import {Power3, Ease} from "gsap";
import {IGGSymbolStates} from "./symbols/GGReelSymbol";
declare module "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio" {
    export enum WinRatioType {
        SUPER_SUPER_MEGA_WIN = "superSuperMegaWin",
        SUPER_SUPER_SUPER_MEGA_WIN = "superSuperSuperMegaWin"
    }
}

const temp = WinRatioType as any;
temp["SUPER_SUPER_MEGA_WIN"] = "superSuperMegaWin";
export class GGGameConfig implements IGameConfig {

    public static instance:GGGameConfig = new GGGameConfig();

    BROWSER_FPS:number = 30;
    GAME_WIDTH:number = 720;
    GAME_HEIGHT:number = 720;
    INITIAL_REEL_SIZE:number = 3;
    MAX_REEL_SIZE:number = 7;
    BET_WIN_MODE:BetWinMode = BetWinMode.BET_WAY;
    SPIN_MODE:SpinMode = SpinMode.AVALANCHE;

    MULTIPLIER:{ [symName:number]:{ multiplier:number } } = {
        16 : {multiplier : 11},
        44 : {multiplier : 33},
        69 : {multiplier : 55}
    };

    DEFAULT_LAYER_SCALE_OPTION:IDeviceDependentConfig<LayerScaleOption> = {
        desktop : {landscape : LayerScaleOption.FIT_WINDOW},
        mobile : {
            landscape : LayerScaleOption.FIT_WINDOW,
            portrait : LayerScaleOption.REELS_FIT_WINDOW
        }
    };

    LAYERS:IGGLayerConfig = {
        BACKGROUND : {name : "background"},
        FS_ANIMATION : {name : "fsAnimation"},
        NEARWIN_GLOW : {name : "nearwinGlow"},
        REEL_BACKGROUND : {name : "reelBackground"},
        GUARANTEED_DEADWILD : {name : "guaranteedDeadWild"},
        SYMBOLS : {name : "symbols"},
        HIGH_SYMBOLS : {name : "highSymbols"},
        REEL_FOREGROUND : {name : "reelForeground"},
        WILD_METER : {name : "wildMeter"},
        WIN_SYMBOLS : {name : "winSymbols"},
        HIGH_WIN_SYMBOLS : {name : "highWinSymbols"},
        GOBLINS : {name : "goblins"},
        BET_WAYS : {name : "betways"},
        WIN_PRESENTATIONS : {name : "winPresentations"},
        FOREGROUND : {name : "foreground"},
        WIN_PARTICLE : {name : "winParticle"},
        BIG_WIN : {name : "bigWin"},
        WIN_COUNT_UP : {name : "winCountUp"},
        FREESPIN_INTRO : {name : "introScreen"},
        INTRO : {name : "introScreen"},
        TWEAK_MODULE : {name : "tweakModule"}
    };

    REAL_REEL_AREA_HEIGHT:number = 550;
    REEL_AREA_TOP:number = 259;
    REEL_AREA_POS_X:number = 0;
    REEL_AREA_POS_Y:number = 123 - this.REEL_AREA_TOP;
    REEL_AREA_WIDTH:number = 720;
    REEL_AREA_HEIGHT:number = this.REAL_REEL_AREA_HEIGHT + this.REEL_AREA_TOP;
    REEL_AREA_MASK_RECT:PIXI.Rectangle = new PIXI.Rectangle(this.REEL_AREA_POS_X-107, this.REEL_AREA_POS_Y + this.REEL_AREA_TOP+9, this.REEL_AREA_WIDTH + 214, this.REAL_REEL_AREA_HEIGHT-8);


    SYMBOL_STATES:IGGSymbolStates = {
        normal : {
            prio : 0,
            keyword : "normal",
            texturesCreator : (symName:string, stateKey:string) => {
                if(symName.indexOf("W")>0 && symName.length>1){
                    symName = "W3";
                }
                return GGGameAssets.getSymbolStateTextures(symName);
            }
        },
        idle : {
            prio : 1,
            keyword : "idle",
            symbols : ["M1", "M2","M3","M4", "M5","S"],
            texturesCreator : (symName:string, stateKey:string) => {
                return GGGameAssets.getSymbolStateTextures(symName);
            }
        },
        attention : {
            prio : 2,
            keyword : "attention",
            symbols : ["S", "W", "Z"],
            texturesCreator : (symName:string, stateKey:string) => {
                return GGGameAssets.getSymbolStateTextures(symName, stateKey);
            }
        },
        plus : {
            prio : 3,
            keyword : "plus",
            texturesCreator : (symName:string, stateKey:string) => {
                return GGGameAssets.getSymbolStateTextures(symName, stateKey);
            },
            symbols : ["S"]
        },
        spin : {
            prio : 4,
            keyword : "spin",
            texturesCreator : (symName:string, stateKey:string) => {
                return [PIXI.Texture.EMPTY];
            }
        },
        landing : {
            prio : 5,
            keyword : "landing",
            texturesCreator : (symName:string, stateKey:string) => {
                return GGGameAssets.getSymbolStateTextures(symName);
            }
        },
        noWin : {
            prio : 6,
            keyword : "noWin",
            texturesCreator : (symName:string, stateKey:string) => {
                return GGGameAssets.getSymbolStateTextures(symName);
            }
        },
        explode : {
            prio : 7,
            keyword : "explode",
            texturesCreator : (symName:string, stateKey:string) => {
                return GGGameAssets.getSymbolStateTextures(symName);
            }
        },
        win : {
            prio : 8,
            keyword : "win",
            texturesCreator : (symName:string, stateKey:string) => {
                return [PIXI.Texture.EMPTY];
            }
        },
        hidden : {
            prio : 9,
            keyword : "hidden"
        }
    };

    SYMBOL_WIDTH:number = 120;
    SYMBOL_HEIGHT:number = 280;
    SYMBOL_BOTTOM_PADDING:number = 0;

    REELS_NUM:number = 6;
    SPACE_BETWEEN_REELS:number = 0;
    REEL_DISPLAY_POSITIONS:[number, number][] = [
        [this.SYMBOL_WIDTH * 0.5, this.REAL_REEL_AREA_HEIGHT + this.REEL_AREA_TOP],
        [this.SYMBOL_WIDTH * 1.5 + this.SPACE_BETWEEN_REELS, this.REAL_REEL_AREA_HEIGHT + this.REEL_AREA_TOP],
        [this.SYMBOL_WIDTH * 2.5 + this.SPACE_BETWEEN_REELS * 2, this.REAL_REEL_AREA_HEIGHT + this.REEL_AREA_TOP],
        [this.SYMBOL_WIDTH * 3.5 + this.SPACE_BETWEEN_REELS * 3, this.REAL_REEL_AREA_HEIGHT + this.REEL_AREA_TOP],
        [this.SYMBOL_WIDTH * 4.5 + this.SPACE_BETWEEN_REELS * 4, this.REAL_REEL_AREA_HEIGHT + this.REEL_AREA_TOP],
        [this.SYMBOL_WIDTH * 5.5 + this.SPACE_BETWEEN_REELS * 5, this.REAL_REEL_AREA_HEIGHT + this.REEL_AREA_TOP]];
    SYMBOLS_NUM_IN_REEL:[number, number, number, number, number] = [0, 0, 8, 0, 0];

    REEL_SPIN_TIME:number = 1;
    REEL_FAST_SPIN_SPIN_TIME:number = 0.8;
    MIN_STOPPING_TIME:number = 0.9;

    REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DURATION: number = 0.3;
    REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DELAY: number = 0.3;
    REEL_SPIN_START_EASE: Ease = Power3.easeIn;
    REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DURATION: number = 0.2;
    REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DELAY: number = 0;
    REEL_SPIN_STOP_ELASTIC_CONFIG: [number, number] = [1, 1];
    REEL_SPIN_QUICK_STOP_ELASTIC_CONFIG: [number, number] = [1, 1];

    REEL_SPIN_START_SPEED: number = 0.5;
    REEL_SPIN_SPIN_SPEED: number = 0.07;
    REEL_NEAR_WIN_SPIN_SPEED: number = 0.25;
    REEL_SPIN_STOP_SPEED: number = 0.25;
    REEL_SPIN_QUICK_STOP_SPEED: number = 0.23;

    REEL_START_DELAY:number = 0.08;
    REEL_STOP_DELAY: number = 0.08;
    REEL_QUICK_STOP_DELAY: number = 0;
    REEL_NEAR_WIN_STOP_DELAY: number = 0.7;

    REEL_SPIN_START_SYMBOL_DELAY: number = 0.05;
    REEL_FAST_SPIN_START_SYMBOL_DELAY: number = 0.05;
    REEL_SPIN_STOP_SYMBOL_DELAY: number = 0.1;
    REEL_SPIN_QUICK_STOP_SYMBOL_DELAY: number = 0.08;
    REEL_SPIN_NEAR_WIN_STOP_SYMBOL_DELAY: number = 0.7;

    FS_REEL_STOP_DELAY:number = 0.12;

    REEL_SETS:IReelSets = {
        "BASE_REELSET" : [
            ["M1", "L1", "L3", "M5"],
            ["M1", "L1", "L3", "M5"],
            ["M1", "L1", "L3", "M5"],
            ["M1", "L1", "L3", "M5"],
            ["M1", "L1", "L3", "M5"],
            ["M1", "L1", "L3", "M5"]
        ],
        "FREESPIN_REELSET" : [
            ["M1", "L1", "L3", "M1", "L1", "L3"],
            ["M1", "L1", "L3", "M1", "L1", "L3"],
            ["M1", "L1", "L3", "M1", "L1", "L3"],
            ["M1", "L1", "L3", "M1", "L1", "L3"],
            ["M1", "L1", "L3", "M1", "L1", "L3"],
            ["M1", "L1", "L3", "M1", "L1", "L3"]
        ]
    };

    WIN_RATIOS:IWinRatios = {
        normalWin : [
            {
                ratio : 0,
                text : "Tiny Win",
                duration : 0,
                type : WinRatioType.TINY
            },
            {
                ratio : 1.5,
                text : "Small Win",
                duration : 0,
                type : WinRatioType.SMALL
            },
            {
                ratio : 3,
                text : "Medium Win",
                duration : 1.5,
                type : WinRatioType.MEDIUM
            },
            {
                ratio : 5,
                text : "Large Win",
                duration : 1.6,
                type : WinRatioType.LARGE
            }
        ],
        bigWin : [
            {
                ratio : 15,
                text : "Big Win",
                duration : 4.092,
                type : WinRatioType.BIG_WIN
            },
            {
                ratio : 25,
                text : "Mega Win",
                duration : 3.790,
                type : WinRatioType.MEGA_WIN

            },
            {
                ratio : 50,
                text : "Super Mega Win",
                duration : 3.790,
                type : WinRatioType.SUPER_MEGA_WIN
            },
            {
                ratio : 100,
                text : "Super Super Mega Win",
                duration : 3.790,
                type : WinRatioType.SUPER_SUPER_MEGA_WIN
            },
            {
                ratio : 250,
                text : "Super Super Super Super Mega Win",
                duration : 7.599,
                type : WinRatioType.SUPER_SUPER_SUPER_MEGA_WIN
            }
        ]
    };

    NEW_BET_WHEN_ABORT_WIN_PRESENTATIONS:boolean = true;

    // GAME_TWEAKER_CONFIG:IGameTweakerConfig = {
    //     layer : this.LAYERS.TWEAK_MODULE.name,
    //     baseWindowWidth : 400,
    //     moduleHeight : 32,
    //     copyAllButtonText : "Copy All",
    //     buttonSkinIdle : "button",
    //     buttonSkinOver : "button",
    //     buttonSkinDown : "button",
    //     buttonSkinDisabled : "button-disabled"
    // };
    PERFORMANCE_LOGGER:boolean = true;

    // #576
    FAULTY_WIN_CAP: boolean = false;

    private constructor() {
        if(GGGameConfig.instance) {
            throw new Error("GGGameConfig.constructor() - Instantiation failed: Singleton.");
        }
    }
}